// Bloodborne pathogens / needle-stick / PEP — pre→post educational-intervention KAP instrument.
// STARTER DRAFT pending expert validation. Grounded in current mainstream guidance
// (WHO/CDC standard precautions & PEP; India NACO PEP guidelines). No correct
// answers are stored in this file.
import { baseDemographics, TF, mc, same } from './common';

// Offered on the clinic-behaviour practice items (PQ1/PQ2/PQ6) so pre-clinical
// students aren't forced to invent an answer. Scored as not-applicable — the
// `score` Edge Function drops it from that student's practice denominator rather
// than counting it wrong (value must equal PRACTICE_NA in score/keys.ts).
const NA_CLINIC = { value: 'na_clinic', label: "I haven't started clinical work yet" };

const demographics = [
  ...baseDemographics,
  {
    key: 'A4',
    field: 'hepBVaccine',
    type: 'single',
    stem: 'Your hepatitis B vaccination status',
    required: true,
    options: [
      { value: 'complete', label: 'Fully vaccinated (3 doses) and titre checked' },
      { value: 'complete_no_titre', label: 'Fully vaccinated (3 doses), titre not checked' },
      { value: 'partial', label: 'Partially vaccinated (1–2 doses)' },
      { value: 'none', label: 'Not vaccinated' },
      { value: 'unsure', label: 'Not sure' },
    ],
  },
];

const knowledge = [
  {
    key: 'KQ1',
    type: 'single',
    options: TF,
    stem: 'Hepatitis B, hepatitis C and HIV can all be transmitted through a needle-stick injury.',
  },
  {
    key: 'KQ2',
    type: 'single',
    options: mc(
      'Hepatitis B',
      'Hepatitis C',
      'HIV',
      'They all carry the same risk',
    ),
    stem: 'Following a single needle-stick from a known positive source, which pathogen carries the highest risk of percutaneous transmission?',
  },
  {
    key: 'KQ3',
    type: 'single',
    options: mc(
      'About 0.3%',
      'About 1.8%',
      'About 6–30%',
      'About 50%',
    ),
    stem: 'The approximate risk of acquiring HIV from a single percutaneous exposure to HIV-positive blood is:',
  },
  {
    key: 'KQ4',
    type: 'single',
    options: TF,
    stem: 'Immediately after a needle-stick injury, the wound should be washed with soap and running water.',
  },
  {
    key: 'KQ5',
    type: 'single',
    options: TF,
    stem: 'Squeezing or sucking the wound after a needle-stick injury reduces the risk of infection.',
  },
  {
    key: 'KQ6',
    type: 'single',
    options: TF,
    stem: 'Applying bleach or another caustic agent (e.g. antiseptic into the wound) is recommended first aid for a needle-stick injury.',
  },
  {
    key: 'KQ7',
    type: 'single',
    options: mc(
      'Within 2 hours, and no later than 72 hours',
      'Within 7 days',
      'Within 2 weeks',
      'Timing does not affect effectiveness',
    ),
    stem: 'For a significant HIV exposure, post-exposure prophylaxis (PEP) is most effective when started:',
  },
  {
    key: 'KQ8',
    type: 'single',
    options: mc(
      '3 days',
      '7 days',
      '28 days',
      '6 months',
    ),
    stem: 'The standard recommended duration of an HIV PEP drug regimen is:',
  },
  {
    key: 'KQ9',
    type: 'single',
    options: TF,
    stem: 'A specific antiviral post-exposure prophylaxis regimen is available to prevent hepatitis C after exposure.',
  },
  {
    key: 'KQ10',
    type: 'single',
    options: mc(
      'Hepatitis B vaccination',
      'Taking antibiotics before procedures',
      'Wearing two pairs of gloves at all times',
      'Annual blood transfusion screening',
    ),
    stem: 'The single most effective measure to protect a dental worker against an occupationally transmitted bloodborne pathogen is:',
  },
  {
    key: 'KQ11',
    type: 'single',
    options: mc(
      'Two-handed recapping is the safest technique',
      'A single-hand scoop technique or a recapping device should be used',
      'Needles should always be recapped using both hands and force',
      'Recapping is never necessary in any situation',
    ),
    stem: 'When a needle must be recapped, current safe practice is that:',
  },
  {
    key: 'KQ12',
    type: 'single',
    options: TF,
    stem: 'Used sharps should be discarded into a designated puncture-proof container rather than a regular waste bag.',
  },
  {
    key: 'KQ13',
    type: 'single',
    options: TF,
    stem: 'Every blood or body-fluid exposure should be reported and risk-assessed, even if the source patient appears healthy.',
  },
];

// Attitude — 8 items. Reverse-coded (negatively-worded): AQ3, AQ4, AQ5 (see keys.ts).
// The "I feel confident…" items moved to the self-efficacy subscale below (v2.0).
const attitude = [
  {
    key: 'AQ1',
    type: 'likert',
    stem: 'Every needle-stick injury should be formally reported, regardless of how minor it seems.',
  },
  {
    key: 'AQ2',
    type: 'likert',
    stem: 'Hepatitis B vaccination should be mandatory for all dental students and staff before clinical work.',
  },
  {
    key: 'AQ3',
    type: 'likert',
    stem: 'Reporting a needle-stick injury is a waste of time because the risk is usually very low.',
  },
  {
    key: 'AQ4',
    type: 'likert',
    stem: 'A colleague who acquires a bloodborne infection at work was probably careless and is partly to blame.',
  },
  {
    key: 'AQ5',
    type: 'likert',
    stem: 'I would feel uncomfortable treating a patient known to be HIV or hepatitis positive.',
  },
  {
    key: 'AQ6',
    type: 'likert',
    stem: 'Standard precautions should be applied to every patient, not only those known to be infectious.',
  },
  {
    key: 'AQ7',
    type: 'likert',
    stem: 'Starting PEP promptly after a high-risk exposure is worthwhile even though the medication can have side effects.',
  },
  {
    key: 'AQ8',
    type: 'likert',
    stem: 'I would value more hands-on training or drills on preventing and managing needle-stick injuries.',
  },
];

// Self-efficacy / confidence — 5 items mapped to the topic's core competencies. Summed
// (max 25), no reverse-coding; reported as a separate construct from attitude (v2.0).
const selfEfficacy = [
  {
    key: 'SE1',
    type: 'likert',
    stem: 'I feel confident that I know the correct immediate first-aid steps to take after a needle-stick injury.',
  },
  {
    key: 'SE2',
    type: 'likert',
    stem: 'I feel confident that I know how and where to access post-exposure prophylaxis if I needed it.',
  },
  {
    key: 'SE3',
    type: 'likert',
    stem: 'I feel confident using safe sharps practice (a single-hand scoop or recapping device, and correct sharps disposal).',
  },
  {
    key: 'SE4',
    type: 'likert',
    stem: 'I feel confident reporting and risk-assessing a blood or body-fluid exposure through the official channel.',
  },
  {
    key: 'SE5',
    type: 'likert',
    stem: 'I feel confident applying standard precautions consistently with every patient.',
  },
];

const practice = [
  {
    key: 'PQ1',
    type: 'single',
    stem: 'How often do you recap needles using a two-handed technique?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NA_CLINIC],
  },
  {
    key: 'PQ2',
    type: 'single',
    stem: 'How do you usually dispose of used needles and other sharps?',
    options: [
      { value: 'sharps', label: 'In a designated puncture-proof sharps container' },
      { value: 'general', label: 'In the general clinical waste bag' },
      { value: 'recap_keep', label: 'Recap and set aside for someone else to clear' },
      { value: 'varies', label: 'It varies / no fixed practice' },
      NA_CLINIC,
    ],
  },
  {
    key: 'PQ3',
    type: 'single',
    stem: 'If you sustained a needle-stick injury right now, what would you do first?',
    options: [
      { value: 'wash', label: 'Wash the wound with soap and running water' },
      { value: 'squeeze', label: 'Squeeze or suck the wound to express blood' },
      { value: 'bleach', label: 'Apply bleach or a strong antiseptic into the wound' },
      { value: 'ignore', label: 'Ignore it and carry on if it looks minor' },
    ],
  },
  {
    key: 'PQ4',
    type: 'single',
    stem: 'The last time you had (or if you ever had) a sharps or splash exposure, did you report it through the official channel?',
    options: [
      { value: 'reported', label: 'Yes, I reported it' },
      { value: 'not_reported', label: 'No, I did not report it' },
      { value: 'never_exposed', label: 'I have never had an exposure' },
    ],
  },
  {
    key: 'PQ5',
    type: 'single',
    stem: 'Have you completed the full course of hepatitis B vaccination?',
    options: [
      { value: 'yes', label: 'Yes, all doses completed' },
      { value: 'partial', label: 'Started but not completed' },
      { value: 'no', label: 'No' },
      { value: 'unsure', label: 'Not sure' },
    ],
  },
  {
    key: 'PQ6',
    type: 'single',
    stem: 'How consistently do you wear appropriate personal protective equipment (gloves, mask, eye protection) during clinical procedures?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NA_CLINIC],
  },
  {
    key: 'PQ7',
    type: 'single',
    stem: 'Do you know the location of your institution’s needle-stick injury / PEP reporting point?',
    options: same('Yes, exactly', 'Roughly', 'No'),
  },
  {
    key: 'IN1',
    type: 'likert',
    stem: 'When I am in clinical practice, I intend to report every needle-stick or splash exposure through the official channel, however minor it seems.',
  },
  {
    key: 'IN2',
    type: 'likert',
    stem: 'If exposed, I intend to apply correct first aid (wash, do not squeeze) and seek post-exposure-prophylaxis assessment within the recommended window.',
  },
];

const education = [
  {
    id: 'pep-risks',
    title: 'Know the risks',
    points: [
      'Hepatitis B, hepatitis C and HIV can all be transmitted by a needle-stick. From a single exposure to a positive source the risk is highest for hepatitis B (about 6–30%), lower for hepatitis C, and about 0.3% for HIV.',
      'Hepatitis B vaccination is the single most effective protection — complete the full three-dose course and confirm your titre.',
      'There is PEP for HIV and a vaccine for hepatitis B, but no specific post-exposure regimen exists for hepatitis C — which is why prevention matters most.',
    ],
    tip: 'Transmission risk ranks B > C > H: hepatitis B highest, hep C middle, HIV lowest (~0.3%). The hep-B vaccine is your armour.',
  },
  {
    id: 'pep-first-aid',
    title: 'First aid — the first 60 seconds',
    points: [
      'Wash the wound immediately with soap and running water.',
      'Do NOT squeeze or suck the wound, and do NOT apply bleach or caustic antiseptics — they do not reduce infection risk and can cause harm.',
      'Report and risk-assess every exposure, even if the source patient appears healthy.',
    ],
    tip: 'Wash, don’t squash — and don’t slosh: soap and water; never squeeze, suck, or bleach.',
  },
  {
    id: 'pep-hiv',
    title: 'HIV PEP — time matters',
    points: [
      'Start PEP as soon as possible — ideally within 2 hours and no later than 72 hours after exposure.',
      'The standard HIV PEP course lasts 28 days. It is worth starting promptly after a high-risk exposure despite possible side-effects.',
    ],
    tip: 'PEP = 72 & 28: start within 72 hours, take it for 28 days. Sooner is better.',
  },
  {
    id: 'pep-prevent',
    title: 'Prevent the next injury',
    points: [
      'Never recap two-handed. If a needle must be recapped, use a single-hand scoop technique or a recapping device.',
      'Discard sharps straight into a designated puncture-proof container — never a regular waste bag.',
      'Apply standard precautions and full PPE to every patient, not only those known to be infectious.',
    ],
    tip: 'Scoop, never two-hand recap; sharps in the box; PPE + hep-B for all.',
  },
];

export default {
  slug: 'pep',
  code: 'KS03',
  title: 'Effectiveness of an Educational Intervention in Improving Knowledge, Attitude, and Practice of Bloodborne-Pathogen Safety, Needle-Stick Injury Management, and Post-Exposure Prophylaxis Among Dental Students and Interns: A Pre–Post Interventional Study',
  shortTitle: 'Needle-stick & PEP',
  studyTitle: 'KAP on Bloodborne Pathogens, Needle-Stick Injuries & PEP',
  topic: 'Occupational safety & infection control',
  emoji: '🩸',
  accent: '#FF6B6B',
  estMinutes: 12,
  mode: 'pre-post',
  status: 'live',
  version: '2.0',
  practiceMax: 4, // scored practice items (PQ1,PQ2,PQ3,PQ5); keep in sync with keys.ts (drift-tested)
  blurb: 'Bloodborne-pathogen risk, needle-stick injury response, and post-exposure prophylaxis — with a built-in learning module that measures what changes.',
  sections: { demographics, knowledge, attitude, selfEfficacy, practice },
  education,
  educationTitle: 'Needle-stick injuries and PEP: the essentials',
  educationBlurb: 'A two-minute refresher based on WHO/CDC standard precautions and India’s NACO post-exposure prophylaxis guidance. Read through, then continue to the short follow-up.',
};
